import { ComponentPropsWithoutRef, forwardRef, useImperativeHandle, useState, useEffect, useRef } from 'react';
import { CurrencyInput } from './CurrencyInput';
import { percentageStringToNumber } from '../../../../util';
import { InputProps } from './Input';
import { InputContainer } from './components';
import { elementsInputClasses } from './util';

export interface PercentageInputProps extends Omit<InputProps, 'inputProps' | 'onChange'> {
  value?: number | null;
  decimalsLimit?: number;
  placeholder?: string;
  onChange?: (value: number | null) => void;
  inputProps?: Omit<ComponentPropsWithoutRef<'input'>, 'onClick' | 'name' | 'disabled' | 'onChange' | 'onBlur' | 'id'>;
  allowNegativeValue?: boolean;
}

export const PercentageInput = forwardRef<HTMLInputElement, PercentageInputProps>(
  (
    {
      name,
      value,
      decimalsLimit = 2,
      onChange,
      inputProps,
      placeholder,
      allowNegativeValue,
      ...inputContainerProps
    },
    forwardRef
  ) => {
    const [localInput, setLocalInput] = useState<string | null>(value?.toString() || null);

    const ref = useRef<HTMLInputElement>(null);
    useImperativeHandle(forwardRef, () => ref.current as HTMLInputElement);

    useEffect(() => {
      // Reset the display when the controlled value is cleared externally (e.g. unlinking an
      // income source clears the percentage).
      if (value === null || value === undefined) {
        setLocalInput(null);
        return;
      }
      // Otherwise adopt the incoming value whenever it differs from what's displayed, so an
      // external change (5 -> 10) reaches the input rather than leaving a stale 5 on screen.
      // Compared numerically on purpose: a keystroke echoes its own value back through onChange,
      // and a string compare would rewrite mid-typed decimals ('1.50' -> '1.5', '1.' -> '1').
      if (localInput === null || localInput === undefined || percentageStringToNumber(localInput) !== value) {
        setLocalInput(value.toString());
      }
    }, [value]);

    return (
      <InputContainer isReadOnly={!!inputProps?.readOnly} {...inputContainerProps}>
        <CurrencyInput
          allowNegativeValue={allowNegativeValue}
          ref={ref}
          id={name}
          readOnly={inputProps?.readOnly}
          // `react-currency-input-field` goes uncontrolled on `undefined` and keeps its last
          // displayed value, so a controlled clear to `null` (e.g. unlinking an income source)
          // never visually clears — map it to '' instead (mirrors MonetaryInput's fix).
          value={localInput ?? ''}
          suffix="%"
          name={name}
          placeholder={placeholder}
          decimalsLimit={decimalsLimit}
          maxLength={3 + decimalsLimit}
          defaultValue={localInput || undefined}
          className={elementsInputClasses.input}
          onValueChange={(value, _, values) => {
            setLocalInput(value || null);
            onChange?.(values?.float || null);
            if (value?.endsWith('%')) {
              ref.current?.setSelectionRange(value.length - 1, value.length - 1);
            }
          }}
          transformRawValue={(v) => {
            const num = percentageStringToNumber(v);
            if (num === 100 || (num > 100 && !v.includes('.'))) {
              return '100';
            }
            return v;
          }}
        />
      </InputContainer>
    );
  }
);
